/* page-prefetch.js — always hold ONE CATALOGUE PAGE EITHER SIDE of the one
 * you are looking at, so paging through a collection feels like scrolling it.
 *
 * The subtlety this exists for: on a paginated PLP the "Next" link is NOT what
 * the browser actually fetches when you click it. section-render.js intercepts
 * [data-section-link] and requests `?page=N&sections=<id>` — a different URL
 * from the document — then morphs the result in. Prefetching the DOCUMENT
 * would therefore warm a cache entry the click never touches. So we warm the
 * thing the click really asks for (the section payload, straight into
 * SectionRender's own memo) and keep the document prefetch only as the
 * fallback for the path where the interception is unavailable.
 *
 * Layering, in the order the browser is asked:
 *   1. SectionRender.fetchSection(next/prev)  — what the click actually uses
 *   2. Speculation Rules `prefetch` (list form, immediate) — the two document
 *      URLs, for the no-JS-interception fallback and for a real navigation
 *   3. <link rel=prefetch> — same two URLs where Speculation Rules are absent
 *
 * Not covered here on purpose: product links. layout/theme.liquid already
 * carries a Speculation Rules `prefetch` at `moderate` eagerness for
 * /products/* and /collections/* (hover intent), with instant-nav.js as the
 * hover/touch fallback where the API is missing. Adding a fourth mechanism on
 * top of those would just re-fetch what is already warm.
 *
 * PASSWORD GATE: while the storefront is password-gated the gate serves
 * `no-store` HTML. That kills prefetch, prerender and bfcache outright, so
 * NONE of layer 2 or 3 does anything on the dev store today. Layer 1 is a
 * plain fetch() into a JS Map and is unaffected — it is the only part of this
 * file that can be observed working before launch.
 */
(() => {
  'use strict';

  // --- guardrails: never spend someone else's data on a page they may not visit
  var conn = navigator.connection || {};
  if (conn.saveData) return;
  if (conn.effectiveType && /(^|-)2g$/.test(conn.effectiveType)) return;
  try { if (matchMedia('(prefers-reduced-data: reduce)').matches) return; } catch (e) { /* unsupported */ }
  // crawlers pay for our speculation and never benefit from it
  if (navigator.webdriver) return;
  if (/bot|crawl|spider|slurp|bingpreview|lighthouse|headless/i.test(navigator.userAgent)) return;

  var done = new Set();          // document URLs already handed to the browser
  var sectionDone = new Set();   // section payloads already warmed
  var MAX = 12;                  // a long paging session must not grow forever

  function adjacentLinks() {
    var out = [];
    ['prev', 'next'].forEach(function (rel) {
      var a = document.querySelector('.pagination a[rel="' + rel + '"]');
      if (a && a.href && a.origin === location.origin) out.push(a);
    });
    return out;
  }

  // LAYER 1 — the payload the click actually fetches.
  function warmSection(a) {
    if (!window.SectionRender || !a.hasAttribute('data-section-link')) return;
    var host = a.closest('[data-section-id]');
    if (!host) return;
    var key = a.href + '|' + host.dataset.sectionId;
    if (sectionDone.has(key) || sectionDone.size >= MAX) return;
    sectionDone.add(key);
    // fetchSection memoises on the same key the click will use, so a warmed
    // page resolves synchronously from the Map with no second request
    try { window.SectionRender.fetchSection(a.href, host.dataset.sectionId).catch(function () {}); }
    catch (e) { /* a failed warm must never break the page */ }
  }

  // LAYER 2/3 — the document, for a real navigation.
  var specSupported = typeof HTMLScriptElement !== 'undefined'
    && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules');

  function warmDocument(urls) {
    urls = urls.filter(function (u) { return !done.has(u); });
    if (!urls.length || done.size >= MAX) return;
    urls.forEach(function (u) { done.add(u); });
    if (specSupported) {
      // list form + immediate: "these two, now" — this is the whole point of
      // the feature. The document-wide rules in theme.liquid stay at
      // conservative/moderate; blanket eagerness across a link-dense grid
      // OOM-crashes the tab (see the comment there).
      var s = document.createElement('script');
      s.type = 'speculationrules';
      s.textContent = JSON.stringify({ prefetch: [{ urls: urls, eagerness: 'immediate' }] });
      document.head.appendChild(s);
      return;
    }
    var l = document.createElement('link');
    if (!(l.relList && l.relList.supports && l.relList.supports('prefetch'))) return;
    urls.forEach(function (u) {
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'document';
      link.href = u;
      document.head.appendChild(link);
    });
  }

  function run() {
    var links = adjacentLinks();
    if (!links.length) return;
    links.forEach(warmSection);
    warmDocument(links.map(function (a) { return a.href; }));
  }

  var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 300); };
  var schedule = function () { idle(run, { timeout: 2000 }); };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
  // paging forward swaps the section in place, which brings a NEW next/prev
  // pair with it — re-prime against the page you just landed on
  document.addEventListener('section:loaded', schedule);
})();
